import { useEffect, useState } from 'react'
import type { FigmaFileListing, FigmaFrameRef } from '../../../shared/figma'
import type { ImportSectionProps } from '../index'
import { api, ApiError } from '../../lib/api'
import { figmaApi, figmaStatusOf } from './api'
import { posthog } from '../../lib/posthog'
import { navigate } from '../../App'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Checkbox } from '../../components/ui/checkbox'
import { Input } from '../../components/ui/input'
import { ModalActions, ModalEyebrow, ModalLede, ModalTitle } from '../../components/ui/modal'
import { CheckboxCard } from '../../components/ui/checkbox'
import { Field } from '../../components/ui/field'
import { ImportStepFooter, ImportStepHeader } from '../../components/ImportSources'
import { FigmaLogo } from './Tile'
import { FigmaTokenForm } from './TokenForm'
import { Note } from '../../components/ui/note'
import { SelectionBar, SelectionBarAction } from '../../components/ui/selection-bar'
import { cn } from '@/lib/utils'

/** mirrors the server's MAX_FIGMA_FRAMES */
const MAX_FRAMES_PER_IMPORT = 25

function failureReason(caught: unknown, fallback: string): { message: string; connect: boolean } {
  if (caught instanceof ApiError) {
    return { message: String(caught.body.error ?? fallback), connect: caught.body.connect === true }
  }
  return { message: fallback, connect: false }
}

/* Figma as one of the import modal's sources: its own screen — link in,
   optional focus on the linked frame, review before anything lands. The
   connection itself is per user and lives on the Integrations page. */
export function FigmaSection({
  canvasId,
  onReview,
  onBack,
}: {
  canvasId: string
  onReview: (listing: FigmaFileListing) => void
  onBack: () => void
}) {
  const [connected, setConnected] = useState<boolean | null>(null)
  /* a server with an approved Figma app can offer its consent screen too */
  const [oauth, setOauth] = useState(false)
  const [url, setUrl] = useState('')
  /* a node link lists just that target; unchecked, the node id is dropped
     and the whole file's frames are listed for review instead */
  const [onlyLinked, setOnlyLinked] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .integrations()
      .then((status) => {
        const figma = figmaStatusOf(status)
        setConnected(figma.connected)
        setOauth(figma.oauth)
      })
      .catch(() => setConnected(false))
  }, [])

  const hasNode = /node-id=/.test(url)

  async function findFrames() {
    if (busy || !url.trim()) return
    setBusy(true)
    setError(null)
    try {
      let link = url.trim()
      if (hasNode && !onlyLinked) {
        const u = new URL(/^https?:/i.test(link) ? link : `https://${link}`)
        u.searchParams.delete('node-id')
        link = u.toString()
      }
      const listing = await figmaApi.listFrames(canvasId, link)
      posthog.capture('figma_frames_found', { frame_count: listing.frames.length, focused: listing.focused })
      if (!listing.frames.length) {
        setError('No frames found there — link to a page or a frame')
        return
      }
      onReview(listing)
    } catch (e) {
      const reason = failureReason(e, 'could not read the Figma file')
      if (reason.connect) setConnected(false)
      setError(reason.message)
    } finally {
      setBusy(false)
    }
  }

  /** Resolves to the reason when Figma turns the token down. */
  async function connectToken(token: string): Promise<string | null> {
    setBusy(true)
    try {
      await figmaApi.connectToken(token)
      posthog.capture('integration_connected', { provider: 'figma', via: 'token' })
      setConnected(true)
      setError(null)
      return null
    } catch (e) {
      return failureReason(e, 'Couldn’t connect').message
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <ImportStepHeader icon={<FigmaLogo width={17} height={25} />} title="Import a Figma file" />
      <ModalLede>
        {connected === false ? 'Connect Figma once, then paste a file or frame link.' : 'Paste a file or frame link.'}{' '}
        Doop needs view access. Top-level frames on the page you choose land as separate frames, with text kept as text
        and auto layout kept as flex.
      </ModalLede>
      {connected === false ? (
        <div className="mt-[22px] flex flex-col gap-3">
          {oauth && (
            <div className="flex items-center gap-2.5">
              <Button variant="primary" onClick={() => navigate('/integrations')}>
                Connect Figma →
              </Button>
              <Note>One connection for all your canvases.</Note>
            </div>
          )}
          <FigmaTokenForm busy={busy} collapsed={oauth} onSubmit={connectToken} />
        </div>
      ) : (
        <>
          <Field className="mt-[22px]" label="Figma link" labelVariant="form" htmlFor="figma-link">
            <Input
              id="figma-link"
              variant="well"
              inputSize="lg"
              className="font-mono"
              autoFocus
              placeholder="https://www.figma.com/design/…"
              value={url}
              disabled={busy || connected === null}
              onChange={(e) => {
                setUrl(e.target.value)
                setError(null)
              }}
              onKeyDown={(e) => e.key === 'Enter' && void findFrames()}
            />
          </Field>
          {hasNode && (
            <CheckboxCard
              checked={onlyLinked}
              disabled={busy}
              onChange={setOnlyLinked}
              title={
                <>
                  Only the linked frame{' '}
                  <em className="ml-[7px] rounded-full bg-paper-deep px-1.5 py-px text-micro font-medium not-italic text-ink-faint">
                    Optional
                  </em>
                </>
              }
              description="Otherwise every top-level frame in the file is listed for review."
            />
          )}
        </>
      )}
      {error && (
        <Note tone="error" size="sm" className="mt-2.5 block">
          {error}
        </Note>
      )}
      <ImportStepFooter
        note="Components are flattened. Prototype links are not imported. Text keeps its font; a family not on Google Fonts or in your font library falls back until you add it."
        onBack={onBack}
        backDisabled={busy}
      >
        {connected !== false && (
          <Button variant="primary" disabled={busy || !url.trim()} onClick={findFrames}>
            {busy ? 'Reading file…' : 'Fetch frames'}
          </Button>
        )}
      </ImportStepFooter>
    </>
  )
}

/** The frames a Figma link offers, under review — the import modal's view
 *  between "Find frames" and anything landing on the canvas. */
export function FigmaReview({
  canvasId,
  listing,
  onBack,
  onStarted,
}: {
  canvasId: string
  listing: FigmaFileListing
  onBack: () => void
  /** the import is running — it carries on as a task on the canvas */
  onStarted: (frameCount: number) => void
}) {
  /* a link to one page or frame means "this"; a whole file means "choose" */
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(listing.frames.length <= MAX_FRAMES_PER_IMPORT ? listing.frames.map((f) => f.id) : []),
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const overLimit = selected.size > MAX_FRAMES_PER_IMPORT

  function toggle(id: string) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function importSelected() {
    if (busy || !selected.size || overLimit) return
    setBusy(true)
    setError(null)
    const frames = listing.frames.filter((f) => selected.has(f.id)).map((f) => ({ id: f.id, page: f.page }))
    try {
      const started = await figmaApi.importFrames(canvasId, listing.fileKey, frames)
      posthog.capture('figma_import_started', { frame_count: started.frames })
      onStarted(started.frames)
    } catch (e) {
      setError(failureReason(e, 'Figma import failed').message)
      setBusy(false)
    }
  }

  const pages = groupByPage(listing.frames)

  return (
    <>
      <div className="flex flex-col items-start justify-between gap-2.5 sm:flex-row sm:gap-6">
        <div className="flex flex-col gap-[5px]">
          <ModalEyebrow>Review before import</ModalEyebrow>
          <ModalTitle>Choose frames</ModalTitle>
        </div>
        <Badge shape="pill">{listing.fileName}</Badge>
      </div>
      <ModalLede>
        {listing.frames.length} {listing.frames.length === 1 ? 'frame' : 'frames'} found. Up to {MAX_FRAMES_PER_IMPORT}{' '}
        land per import, laid out the way they are in Figma.
      </ModalLede>
      <SelectionBar
        className="mt-5"
        count={selected.size}
        total={listing.frames.length}
        tone={overLimit ? 'error' : 'default'}
        note={overLimit && ` — ${MAX_FRAMES_PER_IMPORT} at most`}
        actions={
          <SelectionBarAction disabled={busy} onClick={() => setSelected(new Set())}>
            Clear
          </SelectionBarAction>
        }
      />
      <div
        className={cn(
          'max-h-[calc(100dvh-390px)] overflow-y-auto rounded-card border border-line bg-paper transition-opacity sm:max-h-[min(350px,calc(100vh-390px))]',
          busy && 'opacity-[0.58]',
        )}
        role="group"
        aria-label="Frames to import"
      >
        {pages.map(([page, frames]) => (
          <div key={page}>
            {page && (
              <div className="flex items-center justify-between border-b border-line bg-paper px-3 py-[7px]">
                <span className="text-caption text-ink-faint">{page}</span>
                <SelectionBarAction
                  className="h-auto"
                  disabled={busy}
                  onClick={() => setSelected((current) => new Set([...current, ...frames.map((f) => f.id)]))}
                >
                  Select page
                </SelectionBarAction>
              </div>
            )}
            {frames.map((frame) => (
              <label
                className="relative grid min-h-[50px] cursor-pointer grid-cols-[20px_minmax(0,1fr)_auto] items-center gap-2.5 border-b border-line bg-surface px-3 py-[9px] last:border-b-0 hover:bg-paper"
                key={frame.id}
              >
                <Checkbox checked={selected.has(frame.id)} disabled={busy} onChange={() => toggle(frame.id)} />
                <b className="block overflow-hidden whitespace-nowrap text-ellipsis text-body text-ink">{frame.name}</b>
                <span className="font-mono text-micro text-ink-faint">
                  {frame.width}×{frame.height}
                </span>
              </label>
            ))}
          </div>
        ))}
      </div>
      {listing.truncated && (
        <Note className="mt-2.5 block">
          This file has more frames than fit here — link to a single page to see all of its frames.
        </Note>
      )}
      {error && (
        <Note tone="error" className="mt-2.5 block">
          {error}
        </Note>
      )}
      <ModalActions className="justify-between">
        <Button variant="ghost" disabled={busy} onClick={onBack}>
          ← Back
        </Button>
        <Button variant="primary" disabled={busy || !selected.size || overLimit} onClick={importSelected}>
          {busy ? 'Starting…' : `Import ${selected.size} ${selected.size === 1 ? 'frame' : 'frames'}`}
        </Button>
      </ModalActions>
    </>
  )
}

function groupByPage(frames: FigmaFrameRef[]): [page: string, frames: FigmaFrameRef[]][] {
  const pages = new Map<string, FigmaFrameRef[]>()
  for (const frame of frames) pages.set(frame.page, [...(pages.get(frame.page) ?? []), frame])
  return [...pages]
}

/* The generic import-modal section (see src/integrations): the link form
   swaps the modal to this integration's own review view. */
export function FigmaImportSection({ canvasId, setReview, onImporting, onBack }: ImportSectionProps) {
  return (
    <FigmaSection
      canvasId={canvasId}
      onBack={onBack}
      onReview={(listing) =>
        setReview(
          <FigmaReview canvasId={canvasId} listing={listing} onBack={() => setReview(null)} onStarted={onImporting} />,
        )
      }
    />
  )
}
